import React from "react";
import { useCallback, useEffect, useState, useMemo } from "react";
import toast from "react-hot-toast";
import { fetchPurposeApi } from "../../Api/PurposeType";
import { fetchDonationTypeApi } from "../../Api/DonationTypes";
import { getDonationTPApi } from "../../Api/DonationTypePurpose";
import {
  addBalanceTransfer,
  updateBalanceTransfer,
  getBalanceTransferById,
} from "../../Api/BalanceTransfer";
import { getAllBankApi } from "../../Api/Bank";
import { getUserByRoleType } from "../../Api/user";
import { getBsByIdApi } from "../../Api/BankStatement";
type Option = {
  id: number;
  value: string;
  label: string;
};
type Options = {
  // id: number;
  id: string;
  name: string;
  parentId: number;
};
type BankData = {
  id: string;
  label: string;
  value: string;
};
type AdminData = {
  id: string;
  label: string;
  value: string;
};
interface DonationTypePurposeInterface {
  id: string;
  donationTypeId: string;
  donationPurposeId: string;
  purposeid: string;
  label: string;
}
const TransferModes: Option[] = [
  { id: 1, value: "Bank", label: "Bank" },
  { id: 2, value: "User", label: "User" },
];
interface BalanceTransferFormProps {
  url: string;
  setformmodal?: React.Dispatch<React.SetStateAction<boolean>>;
  id?: string | null;
  onSuccess?: () => void;
  TransferToBankStatementId?: string | null;
  TransferFromBankStatementId?: string | null;
  onClose: () => void;
}
export const BalanceTransferForm = ({
  url,
  setformmodal,
  id,
  onSuccess,
  TransferToBankStatementId,
  TransferFromBankStatementId,
  onClose,
}: BalanceTransferFormProps) => {
  const [bankData, setBankData] = useState<BankData[]>([]);
  const [adminData, setAdminData] = useState<AdminData[]>([]);
  const [amount, setAmount] = useState<number>(0);
  const [transferedOn, setTransferedOn] = useState<string>("");
  const [desc, setDesc] = useState<string>("");
  const [isEdit, setIsEdit] = useState(false);
  const [loading, setLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [transferFrom, setTransferFrom] = useState<Option | null>(null);
  const [transferTo, setTransferTo] = useState<Option | null>(null);
  const [transferAccountFrom, setTransferAccountFrom] = useState<string | null>(
    null
  );
  const [transferAccountTo, setTransferAccountTo] = useState<string | null>(
    null
  );
  const [donationTypeOptions, setDonationTypeOptions] = useState<Options[]>([]);
  const [purposeOptions, setPurposeOptions] = useState<Options[]>([]);
  const [donationPurpose, setDonationPurpose] = useState<Option | null>(
    null
  ) as any;
  const [donationType, setDonationType] = useState<Option | null>(null) as any;
  const [donationTP, setDonationTP] = useState<DonationTypePurposeInterface[]>(
    []
  );


  const fetchBanks = useCallback(async () => {
    try {
      const response = await getAllBankApi(url);
      const formattedBanks = response.map((bank: any) => ({
        value: bank.id,
        label: `${bank.name} - ${bank.accountNumber}`,
      }));
      setBankData(formattedBanks);
    } catch (error) {
      toast.error("Error fetching bank data.", { id: "bankError" });
      setHasError(true);
    }
  }, [url]);

  const fetchAdmins = useCallback(async () => {
    try {
      const response = await getUserByRoleType(2, url);
      const formattedAdmins = response.map((admin: any) => ({
        value: admin.id,
        label: admin.name,
      }));
      setAdminData(formattedAdmins);
    } catch (error) {
      toast.error("Network Error.", { id: "networkError" });
      toast.error("Error fetching admin data.", { id: "adminError" });
      setHasError(true);
    }
  }, [url]);

  useEffect(() => {
    fetchBanks();
    fetchAdmins();
  }, [fetchBanks, fetchAdmins]);

  useEffect(() => {
    const fetchAllTaxonomies = async () => {
      try {
        const [typeResponse, purposeResponse, fetchDonation] =
          await Promise.all([
            fetchDonationTypeApi(url),
            fetchPurposeApi(url),
            getDonationTPApi("donationTPId", url),
          ]);
        setDonationTypeOptions(typeResponse);
        setPurposeOptions(purposeResponse as any);
        setDonationTP(fetchDonation);
      } catch (error) {
        toast.error("Failed to load donation types and purposes", {
          id: "taxonomyError",
        });
        setHasError(true);
        setLoading(false);
      }
    };
    fetchAllTaxonomies();
  }, [url]);

  const donationOptions = useMemo(
    () =>
      donationTypeOptions
        .filter((resp) =>
          donationTP.some((tp) => tp.donationTypeId === resp.id)
        )
        .map((DonationType) => ({
          value: DonationType.name,
          id: DonationType.id,
          label: DonationType.name,
        })),
    [donationTypeOptions, donationTP]
  );

  const donationPurposeOptions = useMemo(
    () =>
      purposeOptions.map((DonationPurpose) => ({
        value: DonationPurpose.name,
        id: DonationPurpose.id,
        label: DonationPurpose.name,
      })),
    [purposeOptions]
  );

  const fetchBalanceTransfer = useCallback(async () => {
    if (id) {
      try {
        const response = await getBalanceTransferById(id, url);
        if (response) {
          const transferFromMode = TransferModes.find(
            (mode) => mode.value === response.transferFrom
          );
          const transferToMode = TransferModes.find(
            (mode) => mode.value === response.transferTo
          );

          const donationTypeMode =
            donationOptions.find(
              (option) => option.id.toString() === response.donationTypeId
            ) || null;

          const donationPurposeMode =
            donationPurposeOptions.find(
              (option) => option.id.toString() === response.donationPurposeId
            ) || null;

          setTransferFrom(transferFromMode || null);
          setTransferTo(transferToMode || null);

          const transferDate = response.transferedOn
            ? new Date(response.transferedOn)
            : null;

          const formattedDate = transferDate
            ? `${transferDate.getFullYear()}-${String(
                transferDate.getMonth() + 1
              ).padStart(2, "0")}-${String(transferDate.getDate()).padStart(
                2,
                "0"
              )}`
            : "";
          setTransferedOn(formattedDate);
          setTransferAccountFrom(
            transferFromMode?.value === "Bank"
              ? response.transferAccountFrom
              : response.transferAccountFrom
          );
          setTransferAccountTo(
            transferToMode?.value === "Bank"
              ? response.transferAccountTo
              : response.transferAccountTo
          );
          setDonationType(donationTypeMode);
          setDonationPurpose(donationPurposeMode);
          setAmount(response.amount);
          setDesc(response.desc);
          setLoading(false);
          setIsEdit(true);
        }
      } catch (error) {
        toast.error("Error fetching balance transfer data.", {
          id: "balanceTransferError",
        });
        setHasError(true);
        setLoading(false);
      }
    }
  }, [id, donationOptions, donationPurposeOptions, url]);

  useEffect(() => {
    if (id) {
      setIsEdit(true);
      setLoading(true);
    } else {
      setIsEdit(false);
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchBalanceTransfer();
  }, [fetchBalanceTransfer]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (
      !transferAccountFrom ||
      !transferFrom ||
      !transferTo ||
      !transferAccountTo ||
      amount <= 0 ||
      !desc
    ) {
      toast.error("Please fill all fields");
      return;
    }

    try {
      const donationTypeId = donationType ? donationType.id : null;
      if (isEdit) {
        await updateBalanceTransfer(
          url,
          id,
          transferAccountFrom,
          transferFrom.value,
          transferTo.value,
          transferAccountTo,
          donationPurpose.id,
          donationTypeId,
          amount,
          transferedOn,
          desc
        );
        if (onSuccess) onSuccess();
        onClose();
        toast.success("Balance Transfer updated successfully.");
      } else {
        await addBalanceTransfer(
          url,
          transferAccountFrom,
          transferFrom.value,
          transferTo.value,
          transferAccountTo,
          donationPurpose.id,
          donationTypeId,
          amount,
          transferedOn,
          desc,
          TransferToBankStatementId,
          TransferFromBankStatementId
        );
        if (onSuccess) onSuccess();
        onClose();
        toast.success("Balance Transfer added successfully.");
      }
    } catch (error) {
      toast.error("Failed to process balance transfer.");
    }
    handleResetForm();
  };

  const handleResetForm = () => {
    setTransferAccountFrom(null);
    setTransferFrom(null);
    setTransferTo(null);
    setTransferAccountTo(null);
    setDonationPurpose(null);
    setDonationType(null);
    setAmount(0);
    setTransferedOn("");
    setDesc("");
    onClose();
  };

    const fetchBankStatementData = async () =>{
      if (TransferToBankStatementId) {
        try {
          const response = await getBsByIdApi(url, TransferToBankStatementId);
          if (response) {
            setTransferTo(TransferModes.find(mode => mode.value === "Bank") || null)
            setTransferAccountTo(response.bankId);
            setTransferedOn(response.valueDate)
            setAmount(response.transactionAmount);
            setDesc(response.description || "");
          }
        } catch (error) {
          console.error(error);
        }
      }else if (TransferFromBankStatementId) {
        try {
          const response = await getBsByIdApi(url, TransferFromBankStatementId);
          if (response) {
            setTransferFrom(TransferModes.find(mode => mode.value === "Bank") || null)
            setTransferAccountFrom(response.bankId);
            setTransferedOn(response.valueDate)
            setAmount(response.transactionAmount);
            setDesc(response.description || "");
          }
        } catch (error) {
          console.error(error);
        }
      }
    };
  
    useEffect(() => {
      if (TransferToBankStatementId || TransferFromBankStatementId) {
        fetchBankStatementData();
      }
    }, [TransferToBankStatementId,TransferFromBankStatementId]);

  return (
    <div className="px-4">
      <div>
        {loading ? (
          <div className="flex justify-center items-center h-screen">
            <div
              className="flex justify-center items-center h-12 w-12 animate-spin rounded-full border-4 border-solid border-current border-e-transparent align-[-0.125em] text-surface motion-reduce:animate-[spin_1.5s_linear_infinite] text-blue-600"
              role="status"
            >
              <span className="!absolute !-m-px !h-px !w-px !overflow-hidden !whitespace-nowrap !border-0 !p-0 ![clip:rect(0,0,0,0)]">
                Loading...
              </span>
            </div>
          </div>
        ) : hasError ? (
          <div className="flex justify-center items-center h-screen">
            {/* Blank page when there’s a network error */}
          </div>
        ) : (
          <>
            <h2 className="text-3xl font-bold text-blue-600 mb-5 mt-5">
              {isEdit ? "Update Balance Transfer" : "Add New Balance Transfer"}
            </h2>
            <form className="space-y-6" onSubmit={handleSubmit}>
              <div>
                <label className="font-medium text-black">Transfer From</label>
                <select
                  className="w-full mt-2 p-2 border rounded-md focus:outline-none focus:ring focus:ring-blue-500"
                  value={transferFrom?.value || ""}
                  onChange={(e) => {
                    const mode =
                      TransferModes.find(
                        (option) => option.value === e.target.value
                      ) || null;
                    setTransferFrom(mode);
                    setTransferAccountFrom(null);
                  }}
                  required
                >
                  <option value="" disabled>
                    Select From
                  </option>
                  {TransferModes.map((mode) => (
                    <option key={mode.id} value={mode.value}>
                      {mode.label}
                    </option>
                  ))}
                </select>
              </div>

              {transferFrom && (
                <div>
                  <label className="font-medium text-black">
                    {transferFrom.value === "Bank"
                      ? "Select Bank"
                      : "Select Person"}
                  </label>
                  <select
                    className="w-full mt-2 p-2 border rounded-md focus:outline-none focus:ring focus:ring-blue-500"
                    value={transferAccountFrom || ""}
                    onChange={(e) => setTransferAccountFrom(e.target.value)}
                    required
                  >
                    <option value="" disabled>
                      {transferFrom.value === "Bank"
                        ? "Choose Bank"
                        : "Select Person"}
                    </option>
                    {(transferFrom.value === "Bank" ? bankData : adminData).map(
                      (option) => (
                        <option key={option.id} value={option.value}>
                          {option.label}
                        </option>
                      )
                    )}
                  </select>
                </div>
              )}

              <div>
                <label className="font-medium text-black">Transfer To</label>
                <select
                  className="w-full mt-2 p-2 border rounded-md focus:outline-none focus:ring focus:ring-blue-500"
                  value={transferTo?.value || ""}
                  onChange={(e) => {
                    const mode =
                      TransferModes.find(
                        (option) => option.value === e.target.value
                      ) || null;
                    setTransferTo(mode);
                    setTransferAccountTo(null);
                  }}
                  required
                >
                  <option value="" disabled>
                    Transfer To
                  </option>
                  {TransferModes.map((mode) => (
                    <option key={mode.id} value={mode.value}>
                      {mode.label}
                    </option>
                  ))}
                </select>
              </div>

              {transferTo && (
                <div>
                  <label className="font-medium text-black">
                    {transferTo.value === "Bank"
                      ? "Select Bank"
                      : "Select Person"}
                  </label>
                  <select
                    className="w-full mt-2 p-2 border rounded-md focus:outline-none focus:ring focus:ring-blue-500"
                    value={transferAccountTo || ""}
                    onChange={(e) => setTransferAccountTo(e.target.value)}
                    required
                  >
                    <option value="" disabled>
                      {transferTo.value === "Bank"
                        ? "Choose Bank"
                        : "Select Person"}
                    </option>
                    {(transferTo.value === "Bank" ? bankData : adminData).map(
                      (option) => (
                        <option key={option.id} value={option.value}>
                          {option.label}
                        </option>
                      )
                    )}
                  </select>
                </div>
              )}

              <div>
                <label className="font-medium text-black">
                  Donation Purpose
                </label>
                <select
                  id="donation-purpose"
                  required
                  value={donationPurpose?.value || ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    const selectedPurpose = donationPurposeOptions.find(
                      (purpose) => purpose.value === value
                    );

                    if (selectedPurpose) {
                      setDonationPurpose({
                        value: selectedPurpose.value,
                        id: selectedPurpose.id,
                        label: selectedPurpose.value,
                      });

                      setDonationType(null);
                    }
                  }}
                  className="w-full mt-2 p-2 border rounded-md focus:outline-none focus:ring focus:ring-blue-500"
                >
                  <option value="" disabled>
                    Select Donation Purpose
                  </option>
                  {donationPurposeOptions.map((option) => (
                    <option key={option.id} value={option.value}>
                      {option.value}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="font-medium text-black">Donation Type</label>
                <select
                  id="donationType"
                  value={donationType?.value || ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (!value) {
                      setDonationType(null);
                      return;
                    }
                    const selectedType = donationOptions.find(
                      (option) => option.value === value
                    );

                    if (selectedType) {
                      setDonationType(selectedType);
                    }
                  }}
                  className="w-full mt-2 p-2 border rounded-md focus:outline-none focus:ring focus:ring-blue-500"
                >
                  <option value="" disabled>
                    Select Donation Type
                  </option>
                  {(donationPurpose
                    ? donationTP
                        .filter(
                          (dtp) => dtp.donationPurposeId === donationPurpose.id
                        )
                        .map((dtp) =>
                          donationOptions.find(
                            (option) =>
                              option.id.toString() === dtp.donationTypeId
                          )
                        )
                        .filter((type) => type !== undefined)
                    : donationOptions
                  ).map((option) => (
                    <option key={option.id} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="font-medium text-black">Amount</label>
                <input
                  type="number"
                  className="w-full mt-2 p-2 border rounded-md focus:outline-none focus:ring focus:ring-blue-500"
                  value={amount}
                  onChange={(e) => setAmount(Number(e.target.value))}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Date
                </label>
                <input
                  required
                  value={transferedOn}
                  onChange={(e) => setTransferedOn(e.target.value)}
                  type="date"
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="font-medium text-black">Description</label>
                <textarea
                  className="w-full mt-2 p-2 border rounded-md focus:outline-none focus:ring focus:ring-blue-500"
                  value={desc}
                  onChange={(e) => setDesc(e.target.value)}
                  required
                />
              </div>

              <div className="flex justify-start space-x-4 mt-4">
                <button
                  type="submit"
                  className="bg-blue-500 hover:bg-blue-600 text-white py-2 px-6 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-300 focus:ring-offset-2"
                >
                  Save and Close
                </button>
                <button
                  type="button"
                  className="bg-blue-500 hover:bg-blue-600 text-white py-2 px-6 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-2"
                  onClick={handleResetForm}
                >
                  Cancel
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
};
